/**
 * A brand mark for each payment method the checkout can offer, drawn inline as SVG.
 *
 * Inline rather than image files for two reasons. The page's content-security policy allows images
 * only from the API origin and https, and every extra asset is another request on a page judged in
 * its first second. And these marks carry no `<style>` block or shared ids, so — unlike the corporate
 * lockups — several of them can sit on one page without overwriting each other.
 *
 * The marks are simplified renderings in each scheme's colours, sized for a 48×32 tile. They are
 * recognition cues beside the method's name, not a substitute for it: the name is always rendered as
 * text next to the icon.
 */

/** The enum names the API sends in `CheckoutPaymentMethodOption.method`. */
type KnownMethod =
  | 'mada'
  | 'visa'
  | 'mastercard'
  | 'americanexpress'
  | 'applepay'
  | 'googlepay'
  | 'stcpay'
  | 'urpay'
  | 'sadad';

function normalise(method: string): KnownMethod | null {
  const key = method.replace(/[\s_-]/g, '').toLowerCase();

  switch (key) {
    case 'mada':
    case 'visa':
    case 'mastercard':
    case 'americanexpress':
    case 'applepay':
    case 'googlepay':
    case 'stcpay':
    case 'urpay':
    case 'sadad':
      return key;
    case 'amex':
      return 'americanexpress';
    default:
      return null;
  }
}

const WORDMARK_FONT = "'Inter', 'Helvetica Neue', Arial, sans-serif";

/* The Apple mark (simple-icons, CC0), scaled into the tile beside the word "Pay". */
const APPLE_PATH =
  'M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701';

function Mark({ method }: { method: KnownMethod | null }) {
  switch (method) {
    case 'mada':
      return (
        <>
          <rect x="5" y="7" width="15" height="7.5" rx="1.5" fill="#259BD6" />
          <rect x="5" y="17.5" width="15" height="7.5" rx="1.5" fill="#84B740" />
          <text
            x="23"
            y="22.5"
            fontFamily={WORDMARK_FONT}
            fontWeight="700"
            fontSize="11"
            fill="#1B1B1B"
            textLength="20"
            lengthAdjust="spacingAndGlyphs"
          >
            mada
          </text>
        </>
      );

    case 'visa':
      return (
        <text
          x="24"
          y="21.5"
          textAnchor="middle"
          fontFamily={WORDMARK_FONT}
          fontWeight="800"
          fontStyle="italic"
          fontSize="15"
          fill="#1A1F71"
          textLength="32"
          lengthAdjust="spacingAndGlyphs"
        >
          VISA
        </text>
      );

    case 'mastercard':
      return (
        <>
          <circle cx="18" cy="16" r="9.5" fill="#EB001B" />
          <circle cx="30" cy="16" r="9.5" fill="#F79E1B" />
          <path d="M24 8.65 A9.5 9.5 0 0 1 24 23.35 A9.5 9.5 0 0 1 24 8.65 Z" fill="#FF5F00" />
        </>
      );

    case 'americanexpress':
      return (
        <>
          <rect x="4" y="5" width="40" height="22" rx="3" fill="#006FCF" />
          <text
            x="24"
            y="20"
            textAnchor="middle"
            fontFamily={WORDMARK_FONT}
            fontWeight="800"
            fontSize="9.5"
            fill="#FFFFFF"
            textLength="28"
            lengthAdjust="spacingAndGlyphs"
          >
            AMEX
          </text>
        </>
      );

    case 'applepay':
      return (
        <>
          <g transform="translate(6 5.5) scale(0.82)">
            <path d={APPLE_PATH} fill="#000000" />
          </g>
          <text
            x="27.5"
            y="21.5"
            fontFamily={WORDMARK_FONT}
            fontWeight="600"
            fontSize="12.5"
            fill="#000000"
            letterSpacing="-0.3"
          >
            Pay
          </text>
        </>
      );

    case 'googlepay':
      return (
        <>
          <text
            x="7"
            y="22"
            fontFamily={WORDMARK_FONT}
            fontWeight="700"
            fontSize="14"
            fill="#4285F4"
          >
            G
          </text>
          <text
            x="20"
            y="22"
            fontFamily={WORDMARK_FONT}
            fontWeight="600"
            fontSize="13"
            fill="#5F6368"
            letterSpacing="-0.3"
          >
            Pay
          </text>
        </>
      );

    case 'stcpay':
      return (
        <>
          <rect x="4" y="5" width="40" height="22" rx="3" fill="#4F008C" />
          <text
            x="24"
            y="20"
            textAnchor="middle"
            fontFamily={WORDMARK_FONT}
            fontWeight="700"
            fontSize="9.5"
            fill="#FFFFFF"
            textLength="30"
            lengthAdjust="spacingAndGlyphs"
          >
            stc pay
          </text>
        </>
      );

    case 'urpay':
      return (
        <text
          x="24"
          y="21"
          textAnchor="middle"
          fontFamily={WORDMARK_FONT}
          fontWeight="800"
          fontSize="13"
          fill="#00A79D"
          textLength="32"
          lengthAdjust="spacingAndGlyphs"
        >
          urpay
        </text>
      );

    case 'sadad':
      return (
        <>
          <rect x="4" y="5" width="40" height="22" rx="3" fill="#1D9E56" />
          <text
            x="24"
            y="20"
            textAnchor="middle"
            fontFamily={WORDMARK_FONT}
            fontWeight="800"
            fontSize="9.5"
            fill="#FFFFFF"
            textLength="30"
            lengthAdjust="spacingAndGlyphs"
          >
            SADAD
          </text>
        </>
      );

    default:
      /* A generic card for any method without a mark of its own, so the row never looks broken. */
      return (
        <>
          <rect x="7" y="7" width="34" height="19" rx="3" fill="none" stroke="#8896A6" strokeWidth="1.6" />
          <rect x="7" y="12" width="34" height="3.5" fill="#8896A6" />
          <rect x="11" y="19" width="9" height="2.5" rx="1" fill="#A8B4C4" />
        </>
      );
  }
}

export function PaymentMethodIcon({ method }: { method: string }) {
  return (
    <svg
      className="bp-method__mark"
      viewBox="0 0 48 32"
      width="48"
      height="32"
      aria-hidden="true"
      focusable="false"
      /*
        Pinned to LTR whatever the page direction. A scheme's wordmark is Latin artwork, not text to
        be read, and under an RTL document SVG text is laid out from the anchor leftwards — which put
        "mada" over its own bars and "Pay" over the apple.
      */
      direction="ltr"
      style={{ direction: 'ltr' }}
    >
      <Mark method={normalise(method)} />
    </svg>
  );
}
